/**
 * AVA Inspect snaps API (aiva.api AvaInspectSnapsController).
 *
 * The controller resolves the tag in the current facility, which it reads from X-Facility-Id, so
 * every call takes the facility (store.state.assetId) explicitly.
 */
import HttpService from '@Utils/httpService';

const baseUrl = (tagId) => `/api/tags/ava-inspect/${encodeURIComponent(tagId)}/snaps`;

const facilityHeaders = (facilityId) => (facilityId ? { 'X-Facility-Id': facilityId } : {});

/** Server message of a failed call, or `fallback`. */
export function apiErrorMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

export async function fetchSnaps(facilityId, tagId) {
  const { data } = await HttpService.get({ url: baseUrl(tagId), headers: facilityHeaders(facilityId) });
  return data || [];
}

/**
 * @param {string} facilityId
 * @param {string} tagId
 * @param {object} meta       CreateAvaInspectSnapDTO: imageId, imageName, tool, polygon, view, cropPolygon,
 *                            imagePolygon (outline in the source image, 0..1; needed for AI analysis)
 * @param {Blob}   thumbnail  context crop (JPEG)
 */
export async function createSnap(facilityId, tagId, meta, thumbnail) {
  const form = new FormData();
  form.append('snap', JSON.stringify(meta));
  form.append('thumbnail', thumbnail, 'snap.jpg');
  const { data } = await HttpService.post({ url: baseUrl(tagId), data: form, headers: facilityHeaders(facilityId) });
  return data;
}

/**
 * Every finding of the tag's AI analysis runs, reviewed or not (rejected included), each with the
 * other scans it was seen on (`views`). AvaInspectFindingVM on the API.
 */
export async function fetchFindings(facilityId, tagId) {
  const { data } = await HttpService.get({
    url: `/api/tags/ava-inspect/${encodeURIComponent(tagId)}/findings`,
    headers: facilityHeaders(facilityId),
  });
  return data || [];
}

/**
 * The tag's element grade from its newest completed analysis (AvaInspectAssessmentVM: the rounds merged
 * worst-case, with `ri`, `riskCategory`, ...), or null when the tag was never analysed (404).
 */
export async function fetchAssessment(facilityId, tagId) {
  try {
    const { data } = await HttpService.get({
      url: `/api/tags/ava-inspect/${encodeURIComponent(tagId)}/assessment`,
      headers: facilityHeaders(facilityId),
    });
    return data || null;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** Links a finding to an existing inspection tag and returns its inspection projection. */
export async function linkFindingInspection(facilityId, tagId, findingId, inspectionId) {
  const { data } = await HttpService.put({
    url: `/api/tags/ava-inspect/${encodeURIComponent(tagId)}/findings/${encodeURIComponent(findingId)}/inspection`,
    data: { inspectionId },
    headers: facilityHeaders(facilityId),
  });
  return data;
}

export async function deleteSnap(facilityId, tagId, snapId) {
  await HttpService.delete({
    url: `${baseUrl(tagId)}/${encodeURIComponent(snapId)}`,
    headers: facilityHeaders(facilityId),
  });
}
